/* iter55f90 — Analyze Story & Transitions drawer.
 *
 * Runs the backend POST /projects/{pid}/analyze-transitions and
 * displays an overall score + per-transition table with proposed
 * rewrites the writer can copy to the editor.
 *
 * iter55f91 — "Fix all weak transitions in one click".
 *   Adds a button that turns every rewrite with a score below 65 into
 *   a pending editorial suggestion anchored to the FROM chapter's
 *   closing paragraph. The suggestions render inline as tracked
 *   changes in the manuscript body and can be bulk accepted / rejected
 *   from a summary panel here.
 */
import React, { useState } from 'react';
import { Loader2, Sparkles, X, TrendingUp, Wand2, Check, Ban, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const WEAK_THRESHOLD = 65;

export default function AnalyzeTransitionsDrawer({ open, onClose, projectId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // "Fix all weak transitions" state.
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);    // apply-weak response
  const [bulk, setBulk] = useState(null);          // 'accept'|'decline'|null

  const run = async () => {
    if (!projectId) return;
    setLoading(true);
    setResult(null);
    setApplied(null);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/analyze-transitions`,
        {}, { withCredentials: true, timeout: 180000 },
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analysis failed.');
    } finally { setLoading(false); }
  };

  const applyWeak = async () => {
    if (!projectId) return;
    setApplying(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/analyze-transitions/apply-weak`,
        {}, { withCredentials: true, timeout: 120000 },
      );
      setApplied(r.data);
      if (r.data?.created_count) {
        toast.success(`Queued ${r.data.created_count} tracked change${r.data.created_count === 1 ? '' : 's'} across ${r.data.per_chapter?.length || 0} chapter${(r.data.per_chapter?.length || 0) === 1 ? '' : 's'}.`);
      } else {
        toast.info('No weak transitions found — the manuscript already hooks strongly.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not queue the fixes.');
    } finally { setApplying(false); }
  };

  const bulkResolve = async (action) => {
    const ids = (applied?.created || []).map((c) => c.suggestion_id);
    if (!ids.length) return;
    setBulk(action);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/suggestions/bulk-resolve`,
        { action, ids }, { withCredentials: true, timeout: 60000 },
      );
      if (action === 'accept') {
        const applyMsg = r.data.applied === r.data.accepted
          ? `Applied ${r.data.applied}/${r.data.accepted} rewrites to the manuscript.`
          : `Accepted ${r.data.accepted}, applied ${r.data.applied} (some anchors drifted — check the editor).`;
        toast.success(applyMsg);
      } else {
        toast.success(`Declined ${r.data.declined} tracked change${r.data.declined === 1 ? '' : 's'}.`);
      }
      // Clear the applied panel so the button set collapses back.
      setApplied(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed.');
    } finally { setBulk(null); }
  };

  if (!open) return null;

  const weakCount = (result?.transitions || []).filter(
    (t) => (t.score ?? 100) < WEAK_THRESHOLD && (t.rewrite_closing || '').trim(),
  ).length;

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-panel-border bg-card shadow-xl"
          data-testid="analyze-transitions-drawer">
      <div className="flex items-center justify-between border-b border-panel-border px-3 py-2">
        <div className="flex items-center gap-2 text-[13.5px] font-semibold uppercase tracking-wider">
          <TrendingUp className="h-3.5 w-3.5 text-primary" /> Story &amp; transitions
        </div>
        <button onClick={onClose} className="rounded-sm p-1 hover:bg-secondary" data-testid="analyze-transitions-close">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {!result && !loading && (
          <button
            onClick={run}
            className="mx-auto flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[13.5px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
            data-testid="analyze-transitions-run"
          >
            <Sparkles className="h-3.5 w-3.5" /> Analyze story &amp; transitions
          </button>
        )}
        {loading && (
          <div className="flex flex-col items-center gap-2 py-8 text-[13.5px] text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            Claude is reading every chapter and grading the hooks…
          </div>
        )}
        {result && (
          <div className="space-y-4">
            <div className="rounded-md border border-panel-border bg-secondary/40 p-3" data-testid="analyze-transitions-overall">
              <div className="text-[13px] uppercase tracking-wider text-muted-foreground">Overall</div>
              <div className="mt-1 font-mono-stat text-2xl">{result.overall?.score ?? 0}<span className="text-[13px] text-muted-foreground">/100</span></div>
              <p className="mt-1 text-[13.5px] leading-relaxed">{result.overall?.summary || ''}</p>
            </div>

            {/* Fix-all bar — shown when there is at least one weak transition */}
            {weakCount > 0 && !applied && (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3" data-testid="fix-all-weak-transitions-bar">
                <div className="flex items-start gap-2">
                  <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="flex-1">
                    <div className="text-[13.5px] font-semibold text-foreground">
                      {weakCount} weak transition{weakCount === 1 ? '' : 's'} detected
                    </div>
                    <p className="mt-0.5 text-[13.5px] leading-snug text-muted-foreground">
                      Turn every rewrite scoring below {WEAK_THRESHOLD}/100 into a tracked change on the corresponding chapter closing. You can accept or reject each one from the editor.
                    </p>
                    <button
                      onClick={applyWeak}
                      disabled={applying}
                      className="mt-2 inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-[13.5px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
                      data-testid="fix-all-weak-transitions-btn"
                    >
                      {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                      Fix all weak transitions
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Post-apply summary + bulk actions */}
            {applied && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50/60 p-3" data-testid="fix-all-weak-transitions-summary">
                <div className="flex items-center gap-2 text-[13.5px] font-semibold text-emerald-900">
                  <Check className="h-3.5 w-3.5" />
                  {applied.created_count} tracked change{applied.created_count === 1 ? '' : 's'} queued
                </div>
                {applied.created_count > 0 && (
                  <>
                    <ul className="mt-2 space-y-1 text-[13.5px]" data-testid="fix-all-weak-transitions-per-chapter">
                      {(applied.per_chapter || []).map((row) => (
                        <li key={row.chapter_id} className="flex items-center justify-between rounded-sm bg-white/70 px-2 py-1">
                          <span>
                            Ch {row.chapter_number}
                            {row.chapter_title ? ` · ${row.chapter_title}` : ''}
                          </span>
                          <span className="font-mono-stat text-[13px] text-emerald-800">×{row.count}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => bulkResolve('accept')}
                        disabled={bulk !== null}
                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[13px] font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                        data-testid="fix-all-accept-all-btn"
                      >
                        {bulk === 'accept' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        Accept all
                      </button>
                      <button
                        onClick={() => bulkResolve('decline')}
                        disabled={bulk !== null}
                        className="inline-flex items-center gap-1.5 rounded-md border border-panel-border bg-white px-2.5 py-1.5 text-[13px] font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
                        data-testid="fix-all-reject-all-btn"
                      >
                        {bulk === 'decline' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                        Reject all
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="ml-auto inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
                        data-testid="fix-all-open-editor-link"
                      >
                        Open in editor <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="mt-2 text-[13px] italic text-muted-foreground">
                      Each change appears as an amber highlight on the chapter closing. Open the editor to accept individually.
                    </p>
                  </>
                )}
                {applied.created_count === 0 && (
                  <p className="mt-1 text-[13.5px] text-muted-foreground">
                    No new fixes queued — either every transition is already strong or the weak ones already have pending suggestions.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              {(result.transitions || []).map((t) => (
                <div key={`${t.from}-${t.to}`}
                     className="rounded-md border border-panel-border bg-background p-3"
                     data-testid={`analyze-transition-row-${t.from}-${t.to}`}>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="font-semibold">Ch {t.from} → Ch {t.to}</span>
                    <span className={`font-mono-stat ${t.score >= 80 ? 'text-emerald-700' : t.score >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
                      {t.score}/100
                    </span>
                  </div>
                  {t.hook_type && (
                    <div className="mt-0.5 text-[13px] uppercase tracking-wider text-muted-foreground">Hook: {t.hook_type}</div>
                  )}
                  {t.issue && (
                    <div className="mt-1 text-[13.5px] italic text-muted-foreground">{t.issue}</div>
                  )}
                  {t.rewrite_closing && (
                    <div className="mt-2 rounded-sm bg-amber-50 p-2 text-[13.5px] leading-relaxed">
                      <div className="mb-0.5 text-[13px] font-semibold uppercase tracking-wider text-amber-900">
                        Suggested rewrite of the previous chapter's closing
                      </div>
                      {t.rewrite_closing}
                      <button
                        onClick={() => { navigator.clipboard.writeText(t.rewrite_closing); toast.success('Copied — paste into the editor.'); }}
                        className="mt-1 text-[13px] text-primary hover:underline"
                        data-testid={`analyze-transition-copy-${t.from}`}
                      >
                        Copy to clipboard
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={run}
              className="w-full rounded-md border border-panel-border py-1.5 text-[13.5px] hover:bg-secondary"
              data-testid="analyze-transitions-rerun"
            >
              Re-run analysis
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
